/**
 * Step 1: Basic Selection Component
 * Handles Facility, Scope, Category, Fuel/Activity selection
 * This is a large step component (~700 lines extracted from EmissionEntryForm.js)
 */

import { useEffect, useMemo, useState } from 'react';
import { Label } from '../../../../../../components/ui/label';
import { Input } from '../../../../../../components/ui/input';
import { SearchableSelect } from '../../../../../../components/ui/searchable-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../../components/ui/select';
import {
  Building2,
  Calculator,
  Car,
  Factory,
  Flame,
  Leaf,
  Wind,
  Workflow,
  Zap,
} from 'lucide-react';
import { resolveGhgUiState } from '../../../../config/resolveGhgUiState';
import {
  getStandardActivityTypeLabel,
  STANDARD_TYPE_OF_PRODUCT_OPTIONS,
} from '../../../../config/standardGhgFormConfig';
import {
  getWasteDisposalCategoryKey,
  resolveWasteActivityFactors,
} from '../../../../config/wasteActivityTaxonomy';
import { selectClosestScope3Factors } from '../../utils/scope3FactorYear';

const escapeOptionHtml = (value) => String(value ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#039;');

const getCategoryIcon = (category = '') => {
  const normalized = category.toLowerCase();
  if (normalized.includes('mobile')) return Car;
  if (normalized.includes('stationary') || normalized.includes('process')) return Factory;
  if (normalized.includes('fugitive')) return Wind;
  if (normalized.includes('flaring')) return Flame;
  if (normalized.includes('electric') || normalized.includes('energy')) return Zap;
  return Leaf;
};

/**
 * Step 1 Basic Selection Component
 * @param {Object} props - All required state and setter props from parent
 */
export const Step1BasicSelection = ({
  // Facility props
  facilityId,
  setFacilityId,
  facilities,
  
  // Scope props
  scope,
  setScope,
  dynamicScopes,
  disabledScopes = [],
  hasScope3Access,
  setCategory,
  onCategoryChange,
  setFuelId,
  setScope3Method,
  setScope3ActivityType,
  setScope3ActivityId,
  setUseCustomFuel,
  setBiogenicScopeSelection,
  setScope3Subcategory,
  
  // Biogenic props
  biogenicScopeSelection,
  loadingBiogenicCategories,
  
  // Category props
  category,
  categoriesForScope,
  capabilities = {},
  
  // Scope 3 Method props
  scope3Method,
  allocationMethod,
  onC8AllocationMethodChange,
  requiresAssetName,
  assetName,
  setAssetName,
  spendCurrencyConversionMethod = 'standard',
  setSpendCurrencyConversionMethod,
  availableScope3Methods,
  getMethodLabel,
  
  // Activity Type props
  scope3ActivityType,
  availableScope3ActivityTypes,
  
  // Subcategory props
  requiresSubcategory,
  availableSubcategories,
  scope3Subcategory,

  // C11: Type of Product (decision-tree branch)
  typeOfProduct,
  setTypeOfProduct,
  
  // Activity props
  scope3ActivityId,
  scope3EFData = [],
  reportingYear,
  filteredScope3Activities,
  useCustomActivity,
  setUseCustomActivity,
  scope3CustomActivity,
  setScope3CustomActivity,
  fuelSearchTerm,
  setFuelSearchTerm,
  loadingScope3EF,
  
  // Fuel props (non-Scope3)
  fuelId,
  useCustomFuel,
  customFuelName,
  setCustomFuelName,
  customEmissionFactor,
  setCustomEmissionFactor,
  customEmissionFactorUnit,
  setCustomEmissionFactorUnit,
  customSource,
  setCustomSource,
  selectedFuel,
  filteredFuelsForCategory,
  getAvailableEFUnits,
  getQuantityUnitFromEFUnit,
  
  // KPI Access Control props
  kpiCanAccessScope = null,
  kpiAllowedScopes = null,
  filterFacilitiesByScope = null,
  hasFullKPIAccess = true,
  
  // Decision field values (for calculation_methodology)
  decisionFieldValues = {},
  setDecisionFieldValues,
}) => {
  const ghgUiState = resolveGhgUiState({
    capabilities,
    scope,
    biogenicScopeSelection,
    processType: decisionFieldValues.process_type,
    scope3ActivityType,
    scope3Method,
    requiresSubcategory,
    scope3Subcategory,
    hasCategory: Boolean(category),
  });
  const CategoryIcon = getCategoryIcon(category);
  const isC8Category = scope === 'scope3' && /^c8\b/i.test(category || '');
  const wasteCategoryKey = scope === 'scope3'
    ? getWasteDisposalCategoryKey(category)
    : null;
  const usesWasteDisposalTaxonomy = Boolean(wasteCategoryKey);
  const [wasteBaseActivity, setWasteBaseActivity] = useState('');
  const wasteCatalogActivities = useMemo(() => resolveWasteActivityFactors(
    selectClosestScope3Factors(scope3EFData.filter((activity) => (
      usesWasteDisposalTaxonomy
      && getWasteDisposalCategoryKey(activity.category, activity.category_code) === wasteCategoryKey
      && activity.method === scope3Method
      && activity.sub_scope !== 'biogenic'
    )), reportingYear),
  ), [reportingYear, scope3EFData, scope3Method, usesWasteDisposalTaxonomy, wasteCategoryKey]);
  const wasteActivityOptions = useMemo(() => {
    if (!usesWasteDisposalTaxonomy) return [];
    const byName = new Map();
    wasteCatalogActivities.forEach((activity) => {
      const name = activity.activity_name;
      if (!name) return;
      if (!byName.has(name)) byName.set(name, activity);
    });
    return Array.from(byName.values());
  }, [usesWasteDisposalTaxonomy, wasteCatalogActivities]);
  useEffect(() => {
    if (!usesWasteDisposalTaxonomy) {
      setWasteBaseActivity('');
      return;
    }
    const selected = wasteCatalogActivities.find((activity) => activity.id === scope3ActivityId);
    if (selected) {
      setWasteBaseActivity(selected.activity_name);
      if (scope3ActivityType !== selected.activity_type) {
        setScope3ActivityType(selected.activity_type);
      }
    } else if (!wasteActivityOptions.some((activity) => activity.activity_name === wasteBaseActivity)) {
      setWasteBaseActivity('');
    }
  }, [scope3ActivityId, scope3ActivityType, setScope3ActivityType, usesWasteDisposalTaxonomy, wasteActivityOptions, wasteBaseActivity, wasteCatalogActivities]);
  const wasteActivityTypes = useMemo(() => Array.from(new Set(
    wasteCatalogActivities
      .filter((activity) => activity.activity_name === wasteBaseActivity)
      .map((activity) => activity.activity_type)
      .filter((type) => type && type !== 'other'),
  )), [wasteBaseActivity, wasteCatalogActivities]);
  const isC8AllocationApplicable = isC8Category
    && ['activity_basis', 'supplier_basis'].includes(scope3Method);
  const usesDirectFuelLayout = scope === 'scope1'
    || (scope === 'biogenic' && biogenicScopeSelection === 'scope1');
  const usesIndirectBiogenicLayout = scope === 'biogenic' && biogenicScopeSelection === 'scope3';
  const sourceSelectionLayout = usesDirectFuelLayout
    ? 'grid min-w-0 grid-cols-1 items-start gap-4 lg:grid-cols-3'
    : usesIndirectBiogenicLayout
      ? 'grid min-w-0 grid-cols-1 items-start gap-4 lg:grid-cols-3'
      : scope === 'scope3'
        ? `grid min-w-0 grid-cols-1 items-start gap-4 md:grid-cols-2 ${isC8Category || /^c11\b/i.test(category || '') ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`
        : scope === 'scope2'
          ? 'grid min-w-0 grid-cols-1 items-start gap-4 lg:grid-cols-2'
          : 'min-w-0';

  // Filter facilities based on selected scope (if KPI access is restricted)
  const filteredFacilities = useMemo(() => {
    let result = facilities.filter(f => f.is_active !== false);
    
    // Apply KPI-based facility filtering if not full access
    if (!hasFullKPIAccess && filterFacilitiesByScope && scope) {
      result = filterFacilitiesByScope(result, scope);
    }
    
    return result;
  }, [facilities, scope, hasFullKPIAccess, filterFacilitiesByScope]);

  // Filter scopes based on KPI access
  const filteredScopes = useMemo(() => {
    const defaultScopes = dynamicScopes.length > 0 ? dynamicScopes : [
      { code: 'scope1', name: 'Scope 1' },
      { code: 'scope2', name: 'Scope 2' },
      { code: 'biogenic', name: 'Biogenic' },
    ];
    
    let result = defaultScopes
      .filter(s => s.code !== 'scope3' || hasScope3Access)
      .filter(s => !disabledScopes.includes(s.code));
    
    // Apply KPI-based scope filtering if not full access
    if (!hasFullKPIAccess && kpiAllowedScopes && kpiAllowedScopes.length > 0) {
      result = result.filter(s => kpiAllowedScopes.includes(s.code));
    }
    
    return result;
  }, [dynamicScopes, disabledScopes, hasScope3Access, hasFullKPIAccess, kpiAllowedScopes]);

  const scope3MethodOptionsHtml = useMemo(() => (
    `<option value="">Select Method</option>${availableScope3Methods.map((method) => (
      `<option value="${escapeOptionHtml(method)}">${escapeOptionHtml(getMethodLabel(method))}</option>`
    )).join('')}`
  ), [availableScope3Methods, getMethodLabel]);

  const biogenicActivityOptionsHtml = useMemo(() => (
    `<option value="">Select Biogenic Activity (${filteredScope3Activities.length} available)</option>${filteredScope3Activities.map((activity) => (
      `<option value="${escapeOptionHtml(activity.id)}">${escapeOptionHtml(activity.activity)}</option>`
    )).join('')}`
  ), [filteredScope3Activities]);

  const activityTypeOptionsHtml = useMemo(() => (
    `<option value="">Select activity type...</option>${availableScope3ActivityTypes.map((type) => (
      `<option value="${escapeOptionHtml(type)}">${escapeOptionHtml(getStandardActivityTypeLabel(type))}</option>`
    )).join('')}`
  ), [availableScope3ActivityTypes]);

  const subcategoryOptionsHtml = useMemo(() => (
    `<option value="">Select sub-category...</option>${availableSubcategories.map((subcategory) => (
      `<option value="${escapeOptionHtml(subcategory.value)}">${escapeOptionHtml(subcategory.label)}</option>`
    )).join('')}`
  ), [availableSubcategories]);

  const typeOfProductOptionsHtml = useMemo(() => (
    `<option value="">Select type of product...</option>${STANDARD_TYPE_OF_PRODUCT_OPTIONS.map((option) => (
      `<option value="${escapeOptionHtml(option.value)}">${escapeOptionHtml(option.label)}</option>`
    )).join('')}`
  ), []);

  const processTypeOptionsHtml = useMemo(() => (
    `<option value="">Select process type</option>${ghgUiState.renderableProcessTypeOptions.map((option) => (
      `<option value="${escapeOptionHtml(option.value)}"${option.disabled ? ' disabled' : ''}>${escapeOptionHtml(option.label)}</option>`
    )).join('')}`
  ), [ghgUiState.renderableProcessTypeOptions]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {/* Facility */}
        <div className="space-y-2">
          <Label htmlFor="emission-facility-select">Facility <span className="text-red-500">*</span></Label>
          <SearchableSelect
              id="emission-facility-select"
              value={facilityId}
              options={filteredFacilities.map((facility) => ({ value: facility.id, label: `${facility.name}${facility.country ? ` (${facility.country})` : ''}` }))}
              onValueChange={setFacilityId}
              placeholder={filteredFacilities.length ? 'Select Facility' : 'No facilities available for this scope'}
              leadingIcon={<Building2 className="h-4 w-4" />}
              testId="emission-facility-select"
            />
          {!hasFullKPIAccess && filteredFacilities.length === 0 && scope && (
            <p className="text-xs text-amber-600">
              You don&apos;t have access to any facilities for {scope}. Contact your admin.
            </p>
          )}
        </div>

        {/* Scope */}
        <div className="space-y-2">
          <Label>Scope <span className="text-red-500">*</span></Label>
          <div className="flex min-h-10 flex-wrap items-center gap-4">
            {filteredScopes.map(s => (
                <label key={s.code} className="flex cursor-pointer items-center gap-2 text-sm font-normal leading-5 text-stone-800">
                  <input
                    type="radio"
                    value={s.code}
                    checked={scope === s.code}
                    onChange={() => {
                      setScope(s.code);
                      setCategory('');
                      setFuelId('');
                      setScope3Method('');
                      setScope3ActivityType('');
                      setScope3ActivityId('');
                      if (s.code === 'scope2') setUseCustomFuel(false);
                      if (s.code !== 'biogenic') {
                        setBiogenicScopeSelection('');
                      }
                      // Reset facility when scope changes (KPI access may differ)
                      if (!hasFullKPIAccess) {
                        setFacilityId('');
                      }
                    }}
                    className="h-4 w-4 accent-emerald-600"
                    data-testid={`entry-scope-${s.code}`}
                  />
                  <span>{s.name}</span>
                </label>
              ))}
          </div>
          {!hasFullKPIAccess && filteredScopes.length === 0 && (
            <p className="text-xs text-amber-600">
              You don&apos;t have access to any scopes. Contact your admin.
            </p>
          )}
        </div>
        
        {/* Biogenic Scope Selection */}
        {scope === 'biogenic' && (
          <div className="flex min-h-10 flex-wrap items-center gap-6 sm:col-span-2">
            <Label className="shrink-0" data-testid="biogenic-emission-type-label">Select Biogenic Emission Type <span className="text-red-500">*</span></Label>
            <div className="flex min-h-10 flex-wrap items-center gap-6">
              <label className="flex cursor-pointer items-center gap-2 text-sm font-normal leading-5 text-stone-800">
                <input
                  type="radio"
                  value="scope1"
                  checked={biogenicScopeSelection === 'scope1'}
                  onChange={(e) => {
                    setBiogenicScopeSelection(e.target.value);
                    setCategory('');
                    setFuelId('');
                    setScope3Method('');
                    setScope3ActivityId('');
                  }}
                  className="h-4 w-4 accent-emerald-600"
                  data-testid="biogenic-scope-radio-scope1"
                />
                <span>Direct Emissions</span>
              </label>
              <label className={`flex items-center gap-2 text-sm font-normal leading-5 text-stone-800 ${!hasScope3Access ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
                <input
                  type="radio"
                  value="scope3"
                  checked={biogenicScopeSelection === 'scope3'}
                  disabled={!hasScope3Access}
                  onChange={(e) => {
                    setBiogenicScopeSelection(e.target.value);
                    setCategory('');
                    setFuelId('');
                    setScope3Method('');
                    setScope3ActivityId('');
                  }}
                  className="h-4 w-4 accent-emerald-600"
                  data-testid="biogenic-scope-radio-scope3"
                />
                <span>Indirect Emissions</span>
                {!hasScope3Access && (
                  <span className="px-1.5 py-0.5 bg-stone-200 text-stone-600 text-[9px] font-semibold rounded whitespace-nowrap">
                    Not Available
                  </span>
                )}
              </label>
            </div>
            {loadingBiogenicCategories && (
              <p className="text-xs text-stone-500" data-testid="biogenic-categories-loading">Loading biogenic categories...</p>
            )}
          </div>
        )}
      </div>

      <div className={sourceSelectionLayout} data-testid="create-source-selection-row">
      {/* Category */}
      <div className="min-w-0 space-y-2">
        <Label htmlFor="emission-category-select">Category <span className="text-red-500">*</span></Label>
        <SearchableSelect
            id="emission-category-select"
            value={category}
            options={categoriesForScope.map((option) => ({ value: option, label: option }))}
            onValueChange={(value) => {
              if (onCategoryChange) {
                onCategoryChange(value);
                return;
              }
              setCategory(value);
              setFuelId('');
              setScope3Method('');
              setScope3ActivityType('');
              setScope3Subcategory('');
              setTypeOfProduct?.('');
              setScope3ActivityId('');
            }}
            placeholder={categoriesForScope.length ? 'Select Category' : 'No categories available'}
            leadingIcon={<CategoryIcon className="h-4 w-4" />}
            testId="emission-category-select"
          />
      </div>

      {/* Process Emissions: keep Category, Process Type, and Methodology aligned. */}
      {ghgUiState.showProcessType && (
        <div className="min-w-0 space-y-2" data-testid="process-type-section">
          <Label>Process Type <span className="text-red-500">*</span></Label>
          <div className="relative">
            <Workflow className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-violet-600" aria-hidden="true" />
            <select
              value={decisionFieldValues.process_type || ''}
              onChange={(event) => setDecisionFieldValues(prev => ({
                ...prev,
                process_type: event.target.value,
                calculation_methodology: 'using_heat_basis_ncv',
              }))}
              className="h-10 w-full rounded-lg border border-stone-200 bg-stone-50 px-3 pl-10 text-sm font-normal leading-5 text-stone-800 outline-none transition-colors focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
              data-testid="process-type-select"
              dangerouslySetInnerHTML={{ __html: processTypeOptionsHtml }}
            />
          </div>
        </div>
      )}

      {ghgUiState.showProcessType && ghgUiState.showCalculationMethodology && (
        <div className="min-w-0 space-y-2" data-testid="calculation-methodology-section">
          <Label>Calculation Methodology</Label>
          <SearchableSelect
              value={decisionFieldValues.calculation_methodology || 'using_heat_basis_ncv'}
              onValueChange={(value) => setDecisionFieldValues(prev => ({ ...prev, calculation_methodology: value }))}
              options={[
                { value: 'using_heat_basis_ncv', label: 'Using Heat Basis (NCV)' },
                { value: 'using_qty_basis_ef', label: 'Using Qty Basis EF' },
                { value: 'using_carbon_composition', label: 'Using Composition of Carbon' },
              ]}
              leadingIcon={<Calculator className="h-4 w-4 text-blue-600" />}
              testId="calculation-methodology-select"
            />
        </div>
      )}

      {/* Biogenic Indirect: Calculation Method */}
      {scope === 'biogenic' && biogenicScopeSelection === 'scope3' && category && (
        <div className="min-w-0 space-y-2">
          <Label>Calculation Method <span className="text-red-500">*</span></Label>
          <div className="relative">
            <Calculator className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-blue-600" aria-hidden="true" />
            <select
              value={scope3Method}
              onChange={(e) => {
                setScope3Method(e.target.value);
                setScope3ActivityId('');
              }}
              className="h-10 w-full rounded-lg border border-stone-200 bg-stone-50 px-3 pl-10 text-sm font-normal leading-5 text-stone-800 outline-none transition-colors focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
              data-testid="biogenic-scope3-method-select"
              dangerouslySetInnerHTML={{ __html: scope3MethodOptionsHtml }}
            />
          </div>
          {availableScope3Methods.length === 0 && (
            <p className="text-xs text-amber-600">No methods available for this category</p>
          )}
        </div>
      )}

      {/* Biogenic Indirect: Biogenic Activity */}
      {scope === 'biogenic' && biogenicScopeSelection === 'scope3' && scope3Method && (
        <div className="min-w-0 space-y-2">
          <div className="flex items-center justify-between">
            <Label>Biogenic Activity <span className="text-red-500">*</span></Label>
            {scope3Method === 'supplier_basis' && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomActivity}
                  onChange={(e) => {
                    setUseCustomActivity(e.target.checked);
                    if (e.target.checked) {
                      setScope3ActivityId('');
                    } else {
                      setScope3CustomActivity('');
                    }
                  }}
                  className="rounded border-stone-300"
                  data-testid="biogenic-scope3-custom-activity-toggle"
                />
                <span className="text-text-secondary">Use Custom Activity</span>
              </label>
            )}
          </div>
          {scope3Method === 'supplier_basis' && useCustomActivity ? (
            <div className="space-y-1.5">
              <Input
                type="text"
                value={scope3CustomActivity}
                onChange={(e) => setScope3CustomActivity(e.target.value)}
                placeholder="Enter custom activity name..."
                className="bg-stone-50 h-10"
                data-testid="biogenic-scope3-custom-activity-input"
              />
              <p className="text-xs text-text-muted">
                Enter a custom activity name describing the biogenic emission source
              </p>
            </div>
          ) : (
            <div className="relative mt-[18px]">
              <select
                value={scope3ActivityId}
                onChange={(e) => setScope3ActivityId(e.target.value)}
                className="h-10 w-full rounded-lg border border-stone-200 bg-stone-50 px-3 text-sm font-normal leading-5 text-stone-800 outline-none transition-colors focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                data-testid="biogenic-scope3-activity-select"
                dangerouslySetInnerHTML={{ __html: biogenicActivityOptionsHtml }}
              />
            </div>
          )}
          {filteredScope3Activities.length === 0 && !useCustomActivity && (
            <p className="text-xs text-amber-600">
              No biogenic activities found for this category and method
            </p>
          )}
        </div>
      )}

      {/* Scope 3: Method and Activity Selection */}
      {category && scope === 'scope3' && (
        <div className="contents" data-testid="scope3-selection-section">
          {/* Method Selection */}
          <div className="min-w-0 space-y-2">
            <Label>Calculation Method <span className="text-red-500">*</span></Label>
            <Select
              value={scope3Method || undefined}
              onValueChange={(value) => {
                setScope3Method(value);
                onC8AllocationMethodChange?.('');
                setScope3ActivityType('');
                setScope3Subcategory('');
                setTypeOfProduct?.('');
                setScope3ActivityId('');
              }}
            >
              <div className="relative min-w-0">
                <Calculator className="pointer-events-none absolute left-3 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-blue-600" aria-hidden="true" />
                <SelectTrigger
                  className="h-10 w-full min-w-0 rounded-lg border-stone-200 bg-stone-50 pl-10 text-left"
                  data-testid="scope3-method-select"
                >
                  <SelectValue placeholder="Select Method" />
                </SelectTrigger>
              </div>
              <SelectContent
                className="z-[100] max-w-[calc(100vw-2rem)]"
                data-testid="scope3-method-options"
                sideOffset={4}
              >
                {availableScope3Methods.map((method) => (
                  <SelectItem
                    key={method}
                    value={method}
                    data-testid={`scope3-method-option-${method}`}
                  >
                    {getMethodLabel(method)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {availableScope3Methods.length === 0 && category && (
              <p className="text-xs text-amber-600">No methods available for this category in Scope 3 EF table</p>
            )}
          </div>

          {scope3Method === 'spend_basis' && (
            <div className="min-w-0 space-y-2" data-testid="scope3-currency-conversion-method-section">
              <Label>Currency Conversion Method <span className="text-red-500">*</span></Label>
              <Select
                value={spendCurrencyConversionMethod}
                onValueChange={setSpendCurrencyConversionMethod}
              >
                <SelectTrigger className="h-10 w-full min-w-0 rounded-lg border-stone-200 bg-stone-50 text-left" data-testid="scope3-currency-conversion-method-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent data-testid="scope3-currency-conversion-method-options">
                  <SelectItem value="standard" data-testid="scope3-currency-conversion-method-option-standard">Standard Currency Conversion</SelectItem>
                  <SelectItem value="ppp_inflation" data-testid="scope3-currency-conversion-method-option-ppp-inflation">Currency adjusted to Inflation Rate and Purchase Power</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {isC8AllocationApplicable && (
            <div className="min-w-0 space-y-2" data-testid="c8-allocation-method-section">
              <Label>Allocation Method <span className="text-red-500">*</span></Label>
              <Select value={allocationMethod} onValueChange={onC8AllocationMethodChange}>
                <SelectTrigger className="h-10 w-full min-w-0 rounded-lg border-stone-200 bg-stone-50 text-left" data-testid="c8-allocation-method-select">
                  <SelectValue placeholder="Select allocation method" />
                </SelectTrigger>
                <SelectContent data-testid="c8-allocation-method-options">
                  <SelectItem value="entire_quantity" data-testid="c8-allocation-method-option-entire-quantity">Entire Quantity</SelectItem>
                  <SelectItem value="floor_area_share" data-testid="c8-allocation-method-option-floor-area-share">Floor Area Share</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Generic Activity Type filter; C5/C12 use Activity-first disposal taxonomy below. */}
          {scope3Method && !usesWasteDisposalTaxonomy && availableScope3ActivityTypes.length > 0 && (
            <div className="min-w-0 space-y-2">
              <Label>Activity Type <span className="text-red-500">*</span></Label>
              <select
                value={scope3ActivityType}
                onChange={(e) => {
                  setScope3ActivityType(e.target.value);
                  setScope3ActivityId('');
                }}
                className="h-10 w-full rounded-lg border border-stone-200 bg-stone-50 px-3 text-sm font-normal leading-5 text-stone-800 outline-none transition-colors focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                data-testid="scope3-activity-type-filter"
                dangerouslySetInnerHTML={{ __html: activityTypeOptionsHtml }}
              />
            </div>
          )}

          {/* Subcategory Selection (for C8/C10/C11/C13/C14) */}
          {scope3Method && requiresSubcategory && availableSubcategories.length > 0 && (!isC8AllocationApplicable || allocationMethod) && (
            <div className="min-w-0 space-y-2">
              <Label>Sub-category <span className="text-red-500">*</span></Label>
              <select
                value={scope3Subcategory}
                onChange={(e) => {
                  setScope3Subcategory(e.target.value);
                  setScope3ActivityId('');
                  // Resetting subcategory invalidates the type_of_product
                  // path through the decision tree — clear it.
                  setTypeOfProduct?.('');
                }}
                className="h-10 w-full rounded-lg border border-stone-200 bg-stone-50 px-3 text-sm font-normal leading-5 text-stone-800 outline-none transition-colors focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                data-testid="scope3-subcategory-select"
                dangerouslySetInnerHTML={{ __html: subcategoryOptionsHtml }}
              />
            </div>
          )}

          {/* C11 Type of Product (decision-tree branch — activity_basis only).
              Categories C11 needs this to pick between continuous_usage and
              one_time_use formulas. Shown after subcategory selection. */}
          {(() => {
            if (!ghgUiState.showTypeOfProduct) return null;
            return (
              <div className="min-w-0 space-y-2">
                <Label>Type of Product <span className="text-red-500">*</span></Label>
                <select
                  value={typeOfProduct || ''}
                  onChange={(e) => {
                    setTypeOfProduct?.(e.target.value);
                    setScope3ActivityId('');
                  }}
                  className="h-10 w-full rounded-lg border border-stone-200 bg-stone-50 px-3 text-sm font-normal leading-5 text-stone-800 outline-none transition-colors focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                  data-testid="scope3-type-of-product-select"
                  dangerouslySetInnerHTML={{ __html: typeOfProductOptionsHtml }}
                />
              </div>
            );
          })()}

          {/* Activity Selection (from Scope 3 EF) */}
          {scope3Method && (
            <div className="relative min-w-0">
              <Label>Activity <span className="text-red-500">*</span></Label>
              {scope3Method === 'supplier_basis' && scope3ActivityType !== 'others' && (scope === 'scope3' || (scope === 'biogenic' && biogenicScopeSelection === 'scope3')) && (
                <label className="absolute right-0 top-0 flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useCustomActivity}
                    onChange={(e) => {
                      setUseCustomActivity(e.target.checked);
                      if (e.target.checked) {
                        setScope3ActivityId('');
                      } else {
                        setScope3CustomActivity('');
                      }
                    }}
                    className="rounded border-stone-300"
                    data-testid="scope3-custom-activity-toggle"
                  />
                  <span className="text-text-secondary">Use Custom Activity</span>
                </label>
              )}

              {scope3Method === 'supplier_basis' && (useCustomActivity || scope3ActivityType === 'others') && (scope === 'scope3' || (scope === 'biogenic' && biogenicScopeSelection === 'scope3')) ? (
                <div className="mt-2 space-y-2">
                  <Input
                    type="text"
                    value={scope3CustomActivity}
                    onChange={(e) => setScope3CustomActivity(e.target.value)}
                    placeholder={scope3ActivityType === 'others' ? "Enter activity name (e.g., Electric Scooter, Carpooling)..." : "Enter custom activity name..."}
                    className="bg-stone-50 h-10"
                    data-testid="scope3-custom-activity-input"
                  />
                  <p className="text-xs text-text-muted">
                    {scope3ActivityType === 'others' 
                      ? 'Enter a descriptive name for this activity type'
                      : 'Enter a custom activity name describing the emission source'}
                  </p>
                </div>
              ) : (
                <div className="mt-2 min-w-0">
                  <SearchableSelect
                    value={usesWasteDisposalTaxonomy ? wasteBaseActivity : scope3ActivityId}
                    options={(usesWasteDisposalTaxonomy ? wasteActivityOptions : filteredScope3Activities).map((activity) => ({ value: usesWasteDisposalTaxonomy ? activity.activity_name : activity.id, label: activity.activity_name || activity.activity }))}
                    onValueChange={(value) => {
                      if (usesWasteDisposalTaxonomy) {
                        setWasteBaseActivity(value);
                        const matches = wasteCatalogActivities.filter((activity) => activity.activity_name === value);
                        const untyped = matches.find((activity) => activity.activity_type === 'other');
                        setScope3ActivityType(untyped?.activity_type || '');
                        setScope3ActivityId(untyped?.id || '');
                      } else {
                        setScope3ActivityId(value);
                      }
                      setFuelSearchTerm('');
                    }}
                    placeholder={
                      !usesWasteDisposalTaxonomy && availableScope3ActivityTypes.length > 0 && !scope3ActivityType
                        ? 'Select activity type first'
                        : requiresSubcategory && !scope3Subcategory
                          ? 'Select sub-category first'
                          : ghgUiState.requiresTypeOfProduct && !typeOfProduct
                            ? 'Select type of product first'
                            : 'Search or select activity'
                    }
                    searchPlaceholder="Search activities..."
                    disabled={(!usesWasteDisposalTaxonomy && availableScope3ActivityTypes.length > 0 && !scope3ActivityType) || (requiresSubcategory && !scope3Subcategory) || (ghgUiState.requiresTypeOfProduct && !typeOfProduct)}
                    testId="scope3-activity-select"
                    menuAlign="end"
                    menuClassName="max-w-[calc(100vw-2rem)]"
                    autoSizeMenuToOptions
                    wrapOptionLabels
                    searchMatchMode="word-prefix"
                  />
                </div>
              )}
              {loadingScope3EF && (
                <p className="text-xs text-blue-600">Loading activities...</p>
              )}
            </div>
          )}

          {scope3Method && usesWasteDisposalTaxonomy && wasteBaseActivity && wasteActivityTypes.length > 0 && (
            <div className="min-w-0 space-y-2" data-testid={`${wasteCategoryKey}-activity-type-section`}>
              <Label htmlFor={`${wasteCategoryKey}-activity-type-select`}>Activity Type <span className="text-red-500">*</span></Label>
              <select
                id={`${wasteCategoryKey}-activity-type-select`}
                value={scope3ActivityType}
                onChange={(e) => {
                  const nextType = e.target.value;
                  const matching = wasteCatalogActivities.find((activity) => (
                    activity.activity_name === wasteBaseActivity
                    && activity.activity_type === nextType
                  ));
                  setScope3ActivityType(nextType);
                  setScope3ActivityId(matching?.id || '');
                }}
                className="h-10 w-full rounded-lg border border-stone-200 bg-stone-50 px-3 text-sm font-normal leading-5 text-stone-800 outline-none transition-colors focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
                data-testid={`${wasteCategoryKey}-activity-type-select`}
              >
                <option value="">Select activity type...</option>
                {wasteActivityTypes.map((type) => <option key={type} value={type}>{getStandardActivityTypeLabel(type)}</option>)}
              </select>
            </div>
          )}

          {requiresAssetName && scope3Method && (scope3ActivityId || (scope3Method === 'supplier_basis' && scope3CustomActivity?.trim())) && (
            <div className="min-w-0 space-y-2" data-testid="asset-name-selection-section">
              <Label htmlFor="asset-name-input">Asset Name <span className="text-red-500">*</span></Label>
              <Input
                id="asset-name-input"
                value={assetName}
                onChange={(event) => setAssetName(event.target.value)}
                placeholder="Enter asset name or identifier"
                className="h-10 bg-stone-50"
                data-testid="asset-name-input"
              />
            </div>
          )}
        </div>
      )}
      
      {/* Calculation Methodology - For Stationary/Mobile/Flaring OR Process Emissions with venting */}
      {ghgUiState.showCalculationMethodology && !ghgUiState.showProcessType && (
        <div className={`space-y-2 ${usesDirectFuelLayout ? '' : 'mt-4 border-b border-stone-200 pb-6'}`} data-testid="calculation-methodology-section">
          <Label>Calculation Methodology</Label>
          <SearchableSelect
              value={decisionFieldValues.calculation_methodology || 'using_heat_basis_ncv'}
              onValueChange={(value) => setDecisionFieldValues(prev => ({ ...prev, calculation_methodology: value }))}
              options={[
                { value: 'using_heat_basis_ncv', label: 'Using Heat Basis (NCV)' },
                { value: 'using_qty_basis_ef', label: 'Using Qty Basis EF' },
                { value: 'using_carbon_composition', label: 'Using Composition of Carbon' },
              ]}
              leadingIcon={<Calculator className="h-4 w-4 text-blue-600" />}
              testId="calculation-methodology-select"
            />
        </div>
      )}

      {/* Fuel Type - Only show for non-Scope 3, non-biogenic-scope3, non-Process Emissions */}
      {ghgUiState.showFuelSelection && (
        <div className={`relative min-w-0 ${usesDirectFuelLayout || scope === 'scope2' ? '' : 'mt-4 border-b border-stone-200 pb-6'}`}>
            <Label>Fuel Type <span className="text-red-500">*</span></Label>
            {/* Custom Fuel toggle - only for Stationary, Mobile, Fugitive, Flaring */}
            {ghgUiState.showCustomFuel && (
              <label className="absolute right-0 top-0 flex items-center gap-2 cursor-pointer">
                <Flame className="h-3.5 w-3.5 text-amber-600" aria-hidden="true" />
                <input
                  type="checkbox"
                  checked={useCustomFuel}
                  onChange={(e) => {
                    setUseCustomFuel(e.target.checked);
                    if (e.target.checked) {
                      setFuelId('');
                      setFuelSearchTerm('');
                    } else {
                      setCustomFuelName('');
                      setCustomEmissionFactor('');
                      setCustomSource('');
                    }
                  }}
                  className="h-4 w-4 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                  data-testid="use-custom-fuel-toggle"
                />
                <span className="text-sm text-amber-700 font-medium">Use Custom Fuel</span>
              </label>
            )}
          {!useCustomFuel ? (
            <div className="mt-2 min-w-0">
              <SearchableSelect
                value={fuelId}
                options={filteredFuelsForCategory.map((fuel) => ({ value: fuel.id, label: fuel.fuel_name }))}
                onValueChange={(value) => {
                  setFuelId(value);
                  setFuelSearchTerm('');
                }}
                placeholder="Search or select fuel type"
                searchPlaceholder="Search fuel types..."
                testId="emission-fuel-select"
              />
            </div>
          ) : (
            <div className="mt-2" data-testid="custom-fuel-name-section">
              <div>
                <Input
                  id="custom-fuel-name-input"
                  value={customFuelName}
                  onChange={(e) => setCustomFuelName(e.target.value)}
                  placeholder="Enter custom fuel name"
                  className="bg-white"
                  data-testid="custom-fuel-name-input"
                />
              </div>
            </div>
          )}

        </div>
      )}
      </div>

    </div>
  );
};

export default Step1BasicSelection;
